import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Camera, RefreshCw, X, Check, RotateCcw, Upload, AlertCircle } from 'lucide-react';
import { uploadToCloudinary } from '../../services/cloudinary';

interface LiveCameraModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCapture: (url: string) => void;
  title?: string;
  subtitle?: string;
}

export const LiveCameraModal: React.FC<LiveCameraModalProps> = ({
  isOpen,
  onClose,
  onCapture,
  title = 'Live Camera Evidence Capture',
  subtitle = 'Capture clear, well-lit visual evidence for the permanent repair record.'
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [stream, setStream] = useState<MediaStream | null>(null);
  const [facingMode, setFacingMode] = useState<'environment' | 'user'>('environment');
  const [capturing, setCapturing] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const stopCamera = useCallback(() => {
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
      setStream(null);
    }
    setCapturing(false);
  }, [stream]);

  const startCamera = useCallback(async (mode: 'environment' | 'user') => {
    setError(null);
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('Camera API is not supported in this browser or requires HTTPS.');
      }

      let mediaStream: MediaStream;
      try {
        mediaStream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: mode },
            width: { ideal: 1920 },
            height: { ideal: 1080 }
          }
        });
      } catch {
        // Fallback for laptops/desktops with only one standard camera
        mediaStream = await navigator.mediaDevices.getUserMedia({
          video: true
        });
      }

      setStream(mediaStream);
      setCapturing(true);

      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
    } catch (err: any) {
      console.error('Error opening live camera:', err);
      setError(err?.message || 'Unable to access live camera. Please grant camera permission or select a photo.');
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      setPreview(null);
      startCamera(facingMode);
    } else {
      stopCamera();
    }
    return () => {
      stopCamera();
    };
  }, [isOpen, facingMode, startCamera, stopCamera]);

  const toggleFacingMode = () => {
    stopCamera();
    setFacingMode((prev) => (prev === 'environment' ? 'user' : 'environment'));
  };

  const drawWatermarkAndGenerateBase64 = (
    ctx: CanvasRenderingContext2D,
    width: number,
    height: number
  ): string => {
    // Watermark overlay bar
    const barHeight = Math.max(36, Math.floor(height * 0.08));
    ctx.fillStyle = 'rgba(15, 23, 42, 0.85)';
    ctx.fillRect(0, height - barHeight, width, barHeight);

    // Accent line
    ctx.fillStyle = '#f97316';
    ctx.fillRect(0, height - barHeight, width, Math.max(3, Math.floor(barHeight * 0.08)));

    // Watermark text
    const fontSize = Math.max(12, Math.floor(barHeight * 0.35));
    ctx.font = `600 ${fontSize}px sans-serif`;
    ctx.fillStyle = '#f8fafc';
    ctx.textBaseline = 'middle';

    const timestamp = new Date().toISOString().replace('T', ' ').substring(0, 19) + ' UTC';
    const tagText = 'VERISURE FORENSIC AUDIT TRAIL';
    ctx.fillText(tagText, 16, height - barHeight / 2);

    ctx.fillStyle = '#fdba74';
    ctx.textAlign = 'right';
    ctx.fillText(timestamp, width - 16, height - barHeight / 2);

    return canvasRef.current?.toDataURL('image/jpeg', 0.85) || '';
  };

  const handleCaptureSnapshot = () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    
    const w = video.videoWidth || 640;
    const h = video.videoHeight || 480;
    canvas.width = w;
    canvas.height = h;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.drawImage(video, 0, 0, w, h);
    const base64WithWatermark = drawWatermarkAndGenerateBase64(ctx, w, h);

    setPreview(base64WithWatermark);
    stopCamera();
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.onload = () => {
        if (!canvasRef.current) return;
        const canvas = canvasRef.current;
        canvas.width = img.width;
        canvas.height = img.height;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        ctx.drawImage(img, 0, 0);
        const watermarked = drawWatermarkAndGenerateBase64(ctx, img.width, img.height);
        setPreview(watermarked);
        stopCamera();
      };
      img.src = event.target?.result as string;
    };
    reader.readAsDataURL(file);
  };

  const handleConfirmPhoto = async () => {
    if (!preview) return;

    setUploading(true);
    try {
      // Attempt upload to Cloudinary
      const uploadedUrl = await uploadToCloudinary(preview);
      onCapture(uploadedUrl);
      onClose();
    } catch (uploadErr) {
      console.warn('Cloudinary upload bypassed/failed. Using high-fidelity evidence snapshot directly:', uploadErr);
      // Fallback seamlessly to the base64 snapshot so the engineer is never blocked
      onCapture(preview);
      onClose();
    } finally {
      setUploading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-md flex items-center justify-center p-3 sm:p-4 overflow-y-auto">
      <div className="bg-slate-900 border border-slate-800 rounded-3xl max-w-lg w-full p-5 sm:p-6 text-white shadow-2xl relative my-auto">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-orange-500/10 border border-orange-500/30 text-orange-400 text-[10px] font-bold tracking-wider uppercase mb-1.5">
              <Camera size={12} /> Live Device Capture
            </div>
            <h2 className="text-xl font-bold text-white">{title}</h2>
            <p className="text-xs text-slate-400 mt-0.5">{subtitle}</p>
          </div>
          <button
            type="button"
            onClick={() => {
              stopCamera();
              onClose();
            }}
            className="text-slate-400 hover:text-white bg-slate-800 hover:bg-slate-700 w-8 h-8 rounded-full flex items-center justify-center transition"
          >
            <X size={18} />
          </button>
        </div>

        {/* Viewfinder / Preview Section */}
        <div className="relative w-full aspect-[4/3] bg-slate-950 rounded-2xl overflow-hidden border border-slate-800 flex items-center justify-center shadow-inner mb-4">
          {preview ? (
            <div className="relative w-full h-full">
              <img src={preview} alt="Evidence Preview" className="w-full h-full object-contain" />
              <div className="absolute top-2 left-2 bg-black/60 backdrop-blur-sm px-2 py-1 rounded text-[11px] font-mono text-emerald-400 flex items-center gap-1">
                <Check size={12} /> Watermarked & Verified
              </div>
            </div>
          ) : error ? (
            <div className="p-6 text-center space-y-3">
              <div className="w-12 h-12 mx-auto rounded-full bg-rose-500/10 text-rose-400 border border-rose-500/20 flex items-center justify-center">
                <AlertCircle size={24} />
              </div>
              <div>
                <p className="text-sm font-semibold text-rose-300">Camera Access Notice</p>
                <p className="text-xs text-slate-400 mt-1 max-w-xs mx-auto">{error}</p>
              </div>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-500 text-white text-xs font-semibold px-4 py-2.5 rounded-xl transition"
              >
                <Upload size={14} /> Select Photo from Device
              </button>
            </div>
          ) : (
            <div className="relative w-full h-full">
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className="w-full h-full object-cover"
              />
              {/* Camera reticle overlay */}
              <div className="absolute inset-4 border border-dashed border-white/20 rounded-xl pointer-events-none flex flex-col justify-between p-3">
                <div className="flex justify-between text-[10px] text-white/50 font-mono">
                  <span>[REC] 60FPS</span>
                  <span>WATERMARK ACTIVE</span>
                </div>
                <div className="text-center text-[10px] text-white/40 tracking-wider uppercase font-mono">
                  Align component within frame
                </div>
              </div>

              {/* Flip camera button */}
              <button
                type="button"
                onClick={toggleFacingMode}
                title="Switch Camera (Front/Rear)"
                className="absolute top-3 right-3 bg-black/60 hover:bg-black/80 text-white p-2.5 rounded-full border border-white/10 backdrop-blur-sm transition"
              >
                <RefreshCw size={16} />
              </button>
            </div>
          )}
        </div>

        {/* Hidden Canvas for High-Resolution Watermarked Snapshot */}
        <canvas ref={canvasRef} className="hidden" />

        {/* Hidden File Input for Device/Gallery Fallback */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          onChange={handleFileUpload}
          className="hidden"
        />

        {/* Action Controls */}
        {preview ? (
          <div className="flex gap-3">
            <button
              type="button"
              disabled={uploading}
              onClick={() => {
                setPreview(null);
                startCamera(facingMode);
              }}
              className="flex-1 inline-flex items-center justify-center gap-2 bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700 font-semibold py-3 px-4 rounded-xl text-sm transition"
            >
              <RotateCcw size={16} /> Retake
            </button>
            <button
              type="button"
              disabled={uploading}
              onClick={handleConfirmPhoto}
              className="flex-1 inline-flex items-center justify-center gap-2 bg-gradient-to-r from-orange-600 to-amber-600 hover:opacity-95 text-white font-bold py-3 px-4 rounded-xl text-sm shadow-lg shadow-orange-500/20 transition disabled:opacity-50"
            >
              {uploading ? (
                <>
                  <RefreshCw size={16} className="animate-spin" /> Saving Evidence...
                </>
              ) : (
                <>
                  <Check size={16} /> Attach Evidence
                </>
              )}
            </button>
          </div>
        ) : (
          <div className="flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="inline-flex items-center gap-2 text-xs font-semibold text-slate-400 hover:text-white px-3 py-2 rounded-xl bg-slate-800/60 hover:bg-slate-800 border border-slate-700/60 transition"
            >
              <Upload size={14} /> Upload / Choose File
            </button>

            <button
              type="button"
              disabled={!capturing}
              onClick={handleCaptureSnapshot}
              className="inline-flex items-center gap-2 bg-gradient-to-r from-orange-600 to-amber-600 hover:from-orange-500 hover:to-amber-500 disabled:opacity-50 text-white font-bold px-6 py-3 rounded-2xl shadow-lg shadow-orange-500/20 transition text-sm"
            >
              <Camera size={18} />
              <span>Capture Frame</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
